import React, { useState } from 'react';
import { Col, ExperimentalForm, Input, Row, Stack } from 'shared';
import { Upload, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { buildFieldName } from 'Auth/buildFieldName';
import styles from './CompanyFormFields.module.css';
import { Fieldset } from 'App/components/FormHelpers';
import { LetterIcon } from 'App/components/LetterIcon';
import { SentryApiClient } from '_generated/api';
import { useApi } from 'App';
import { useAuthorization } from 'Auth';

interface CompanyFormFieldProps {
  active: boolean;
  visible: boolean;
  namespace?: string | string[];
  name?: string;
  companyId?: string;
}

interface CompanyLogoUploadProps {
  companyId?: number;
  namespace?: string | string[];
}

const CompanyLogoUpload: React.FC<CompanyLogoUploadProps> = ({
  companyId,
  namespace,
}) => {
  const { companyApi } = useApi();
  const [token] = useAuthorization();
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Load existing logo if editing
  React.useEffect(() => {
    const loadExistingLogo = async () => {
      if (!companyId || !token) {
        setLogoUrl(null);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`/api/companies/${companyId}/logo`, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);
          setLogoUrl(imageUrl);
        } else {
          setLogoUrl(null);
        }
      } catch (error) {
        console.error('Error loading existing logo:', error);
        setLogoUrl(null);
      } finally {
        setLoading(false);
      }
    };

    loadExistingLogo();

    // Cleanup function to revoke object URL
    return () => {
      if (logoUrl && logoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(logoUrl);
      }
    };
  }, [companyId, token]);

  const beforeUpload = (file: File) => {
    const isValidType = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
    ].includes(file.type);
    if (!isValidType) {
      message.error('You can only upload JPG/PNG/GIF files!');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return false;
    }
    return true;
  };

  const handleUpload = async (options: any) => {
    if (!companyId) {
      message.error('Please save the company first before uploading a logo');
      return;
    }

    const { file } = options;
    setUploading(true);

    try {
      // Convert File to FileParameter format expected by the API client
      const fileParameter = {
        data: file,
        fileName: file.name,
      };

      await companyApi.uploadLogo(companyId, fileParameter);

      // Fetch the newly uploaded logo with authentication
      const response = await fetch(`/api/companies/${companyId}/logo`, {
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Clean up previous blob URL
        if (logoUrl && logoUrl.startsWith('blob:')) {
          URL.revokeObjectURL(logoUrl);
        }

        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setLogoUrl(imageUrl);
      }

      message.success('Logo uploaded successfully!');
    } catch (error: any) {
      console.error('Logo upload error:', error);
      const errorMessage =
        error?.response?.data || error?.message || 'Failed to upload logo';
      message.error(`Failed to upload logo: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!companyId) return;

    try {
      await companyApi.deleteLogo(companyId);

      // Clean up blob URL
      if (logoUrl && logoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(logoUrl);
      }

      setLogoUrl(null);
      message.success('Logo deleted successfully!');
    } catch (error) {
      message.error('Failed to delete logo');
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload Logo</div>
    </div>
  );

  return (
    <ExperimentalForm.Item
      label={<span>Company Logo</span>}
      extra={
        <span>Upload a logo for your company (JPG, PNG, GIF - max 5MB)</span>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Upload
          name="logoFile"
          listType="picture-card"
          className="company-logo-uploader"
          showUploadList={false}
          beforeUpload={beforeUpload}
          customRequest={handleUpload}
          disabled={!companyId}
        >
          {loading ? (
            <div>
              <div style={{ marginTop: 8 }}>Loading...</div>
            </div>
          ) : logoUrl ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#0f3f76',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={logoUrl}
                alt="Company Logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onError={() => {
                  if (logoUrl && logoUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(logoUrl);
                  }
                  setLogoUrl(null);
                }}
              />
            </div>
          ) : (
            uploadButton
          )}
        </Upload>
        {logoUrl && (
          <DeleteOutlined
            onClick={handleDelete}
            style={{ cursor: 'pointer', color: '#ff4d4f', fontSize: '16px' }}
            title="Delete logo"
          />
        )}
      </div>
      {!companyId && (
        <div style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
          Save the company first to upload a logo
        </div>
      )}
    </ExperimentalForm.Item>
  );
};

const CompanyFormFields: React.FC<CompanyFormFieldProps> = ({
  active,
  visible,
  namespace,
  name,
  companyId,
}) => {
  const { companyApi } = useApi();
  return (
    <Fieldset id={styles.company} active={active} visible={visible}>
      {name && (
        <Stack direction="vertical" alignment="middle" gap={20}>
          <LetterIcon name={name} />
          <Stack.Item />
        </Stack>
      )}
      <ExperimentalForm.Item
        name={buildFieldName(namespace, 'name')}
        label={<span>Company Name</span>}
        rules={[
          {
            required: true,
            message: 'Please add a full name!',
          },
          {
            validator: async (rule, value) => {
              const companiesWithSameName = await companyApi.get(
                null,
                null,
                undefined,
                null,
                [
                  {
                    field: SentryApiClient.CompanyFilters.Name,
                    operator: SentryApiClient.Operator.Equals,
                    value: value as string,
                  },
                  {
                    field: SentryApiClient.CompanyFilters.Id,
                    operator: SentryApiClient.Operator.NotEquals,
                    value: companyId ? companyId : '-1',
                  },
                ]
              );
              if (
                active &&
                visible &&
                companiesWithSameName.items &&
                companiesWithSameName.items.length > 0
              ) {
                throw new Error(
                  'There already exists a company with this name'
                );
              }
            },
          },
        ]}
      >
        <Input />
      </ExperimentalForm.Item>

      <ExperimentalForm.Item
        name={buildFieldName(namespace, 'baseUrl')}
        label={<span>Base URL</span>}
        extra={
          <span>
            The domain URL for this company (e.g., sentrywatertech.com). Used to
            automatically display company logo on login page.
          </span>
        }
      >
        <Input placeholder="Enter domain (e.g., sentrywatertech.com)" />
      </ExperimentalForm.Item>

      <CompanyLogoUpload
        companyId={companyId ? parseInt(companyId) : undefined}
        namespace={namespace}
      />

      <ExperimentalForm.Item
        name={buildFieldName(namespace, ['address', 'address1'])}
        label={<span>Address</span>}
        extra={<span>Line 1</span>}
        rules={[
          {
            required: true,
            message: 'At least address 1 is required',
          },
        ]}
        className={styles.addressRow}
      >
        <Input />
      </ExperimentalForm.Item>
      <ExperimentalForm.Item
        name={buildFieldName(namespace, ['address', 'address2'])}
        colon={false}
        label={<span></span>}
        extra={<span>Line 2</span>}
        className={styles.addressRow}
      >
        <Input />
      </ExperimentalForm.Item>
      <Row className={styles.addressRow}>
        <Col xs={0} sm={7}></Col>
        <Col sm={6} md={5}>
          <ExperimentalForm.Item
            name={buildFieldName(namespace, ['address', 'city'])}
            wrapperCol={{ sm: 24 }}
            colon={false}
            extra={<span>City</span>}
            rules={[
              {
                required: true,
                message: 'Company must be in a city',
              },
            ]}
            className={styles.addressCol + ' ' + styles.addressRow}
          >
            <Input />
          </ExperimentalForm.Item>
        </Col>
        <Col sm={6} md={5}>
          <ExperimentalForm.Item
            name={buildFieldName(namespace, ['address', 'country'])}
            wrapperCol={{ sm: 24 }}
            colon={false}
            extra={<span>Country</span>}
            rules={[
              {
                required: true,
                message: 'Company must be in a country',
              },
            ]}
            className={styles.addressCol + ' ' + styles.addressRow}
          >
            <Input />
          </ExperimentalForm.Item>
        </Col>
      </Row>
      <Row className={styles.addressRow}>
        <Col xs={0} sm={7}></Col>
        <Col sm={6} md={5}>
          <ExperimentalForm.Item
            name={buildFieldName(namespace, ['address', 'region'])}
            colon={false}
            wrapperCol={{ sm: 24 }}
            extra={<span>Region</span>}
            rules={[
              {
                required: true,
                message: 'Need a province or state',
              },
            ]}
            className={styles.addressCol + ' ' + styles.addressRow}
          >
            <Input />
          </ExperimentalForm.Item>
        </Col>
        <Col sm={6} md={5}>
          <ExperimentalForm.Item
            name={buildFieldName(namespace, ['address', 'zip'])}
            colon={false}
            wrapperCol={{ sm: 24 }}
            extra={<span>Postal Code</span>}
            rules={[
              {
                required: true,
                message: 'Need a postal code or zip code',
              },
            ]}
            className={styles.addressCol + ' ' + styles.addressRow}
          >
            <Input />
          </ExperimentalForm.Item>
        </Col>
      </Row>
    </Fieldset>
  );
};

export { CompanyFormFields };
